// src/components/layout/TopBar.tsx
import React from 'react';
import { MenuIcon, ClipboardListIcon, SearchIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUserDisplayName } from '../../hooks/useUserDisplayName';
import { getRoleDefinition } from '../../types/roles';
import { useOpenIntentionPeriod } from '../../contexts/OpenIntentionPeriodContext';
import { useCommandPalette } from './CommandPalette';

interface TopBarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  showSidebarToggle?: boolean; // New optional prop
}
const TopBar: React.FC<TopBarProps> = ({
  sidebarOpen,
  setSidebarOpen,
  showSidebarToggle = true
}) => {
  const {
    user,
    logout
  } = useAuth();
  const userDisplayName = useUserDisplayName();
  const { isOpen } = useOpenIntentionPeriod();
  const { open: openCommandPalette } = useCommandPalette();

  return (
    <>
      <header className="bg-white border-b border-ink-200">
      <div className="flex items-center justify-between h-topbar px-4 sm:px-7">
        <div className="flex items-center gap-3">
          {/* Conditionally show sidebar toggle - only show if sidebar should be visible */}
          {showSidebarToggle && <button type="button" className="inline-flex items-center justify-center p-2 rounded-md text-ink-400 hover:text-ink-700 hover:bg-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-600/55 lg:hidden" onClick={() => setSidebarOpen(true)}>
              <span className="sr-only">Open main menu</span>
              <MenuIcon className="h-6 w-6" aria-hidden="true" />
            </button>}

          <img src="/logo/pit-logo-compact.svg" alt="PIT" className="h-6 w-auto lg:hidden" />

          {/* Command palette trigger */}
          <button
            type="button"
            onClick={openCommandPalette}
            className="hidden sm:inline-flex items-center gap-2 h-[38px] pl-3 pr-2 rounded-md border border-ink-200 text-body-sm text-ink-500 hover:bg-ink-50 hover:text-ink-700 transition-colors"
            aria-label="Search pages"
          >
            <SearchIcon className="h-4 w-4" />
            <span>Search…</span>
            <kbd className="ml-2 text-meta font-mono text-ink-400 border border-ink-200 rounded px-1.5 py-0.5">⌘K</kbd>
          </button>
        </div>

        <div className="flex items-center gap-4">
          {isOpen && (
            <Link
              to="/intentions/submit"
              className="hidden sm:inline-flex items-center gap-2 px-3 h-[38px] rounded-md text-body-sm font-semibold bg-gold-400 text-navy-900 hover:bg-gold-500 transition-colors"
            >
              <ClipboardListIcon className="h-4 w-4" />
              Submit my teaching intentions
            </Link>
          )}

          {/* User menu */}
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-body-sm text-ink-700">
              {userDisplayName}
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-navy-100 text-navy-800">
              {user?.role ? getRoleDefinition(user.role)?.name || user.role.replace('_', ' ') : 'User'}
            </span>
            <button onClick={logout} className="text-body-sm font-medium text-ink-500 hover:text-ink-900 px-3 py-2 rounded-md hover:bg-ink-100 transition-colors">
              Sign out
            </button>
          </div>
        </div>
      </div>
    </header>
    </>
  );
};
export default TopBar;